import React, { useContext } from "react";
import { GenericBanner, useDeviceTypes, SanitizedHtml } from "halifax";
import { RouterProps } from "react-router-dom";
import {
  B2B_PORTAL_BANNER_CONTAINER_ID,
  CustomerAccountRole,
  PATH_PREMIER_COLLECTION_SHOP_DETAILS,
  StayTypesEnum,
} from "redmond";
import { Typography } from "@material-ui/core";
import { isCorpTenant, getCurrentCorpAccount } from "@capone/common";
import { config } from "../../../../api/config";
import * as constants from "./constants";
import { ClientContext } from "../../../../App";

import { RewardsBannerConnectorProps } from "./container";
import {
  PATH_HOME,
  PATH_AVAILABILITY,
  PATH_SHOP,
  PATH_ANCILLARY_CUSTOMIZE,
  PATH_VACATION_RENTALS,
  PATH_VACATION_RENTALS_AVAILABILITY,
  PATH_VACATION_RENTALS_SHOP,
  PATH_BOOK,
  PATH_VACATION_RENTALS_BOOK,
} from "../../../../utils/paths";
import {
  useExperiments,
  getExperimentVariant,
  LC_GATING_EXPERIMENT,
  AVAILABLE,
  PREMIER_COLLECTION_EXPERIMENT,
} from "../../../../context/experiments";

const LIFESTYLE_ONLY_CARD_PRODUCTS = ["Venture Business", "Spark Miles", "Spark Cash Plus"];

export interface IRewardsBannerProps
  extends RewardsBannerConnectorProps,
  RouterProps { }

export const RewardsBanner = (props: IRewardsBannerProps) => {
  const { history, largestValueAccount, rewardsAccounts, stayType } = props;
  const { sessionInfo, isRewardsDisabled } = useContext(ClientContext);
  const firstName = sessionInfo?.userInfo?.firstName || "";

  const { matchesMobile } = useDeviceTypes();
  const expState = useExperiments();

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const isLifestyleOnlyCollectionEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
    AVAILABLE && !isPremierCollectionEnabled;

  const isOnPremiumStaysBannerPath = [
    PATH_HOME.slice(0, -1),
    PATH_HOME,
    PATH_VACATION_RENTALS,
    PATH_AVAILABILITY.slice(0, -1),
    PATH_AVAILABILITY,
    PATH_SHOP.slice(0, -1),
    PATH_SHOP,
    PATH_PREMIER_COLLECTION_SHOP_DETAILS,
    PATH_ANCILLARY_CUSTOMIZE,
    PATH_VACATION_RENTALS_AVAILABILITY,
    PATH_VACATION_RENTALS_SHOP.slice(0, -1),
    PATH_VACATION_RENTALS_SHOP,
    PATH_BOOK.slice(0, -1),
    PATH_BOOK,
    PATH_VACATION_RENTALS_BOOK.slice(0, -1),
    PATH_VACATION_RENTALS_BOOK,
  ].includes(history.location.pathname);

  if (isRewardsDisabled && isOnPremiumStaysBannerPath) {
    return (
      <GenericBanner
        className="rewards-banner-root"
        content={constants.PREMIER_COLLECTION_REWARDS_DISABLED_BANNER_TEXT}
        containerId={B2B_PORTAL_BANNER_CONTAINER_ID}
        isMobile={matchesMobile}
      />
    );
  }

  let contentText;

  const currentAccount = getCurrentCorpAccount(rewardsAccounts, sessionInfo);

  const account = isCorpTenant(config.TENANT)
    ? currentAccount || largestValueAccount
    : largestValueAccount;

  const {
    earn,
    lastFour = "",
    productDisplayName: productId = "",
    rewardsBalance,
    customerAccountRole = "",
  } = account || {};
  const { currency, currencyDescription } = rewardsBalance || {};
  const { hotelsMultiplier, homesMultiplier } = earn || {};

  const earnRate =
    stayType === StayTypesEnum.VacationRentals
      ? homesMultiplier
      : hotelsMultiplier;

  const isCardLifestyleOnly =
    isCorpTenant(config.TENANT) &&
    LIFESTYLE_ONLY_CARD_PRODUCTS.includes(
      account?.productDisplayName ?? ""
    );

  const isWithLifestyle = isPremierCollectionEnabled && !isCardLifestyleOnly;
  const isLifestyleOnly =
    isLifestyleOnlyCollectionEnabled || isCardLifestyleOnly;

  // TODO remove once earn/credit comes from BE
  // eslint-disable-next-line no-nested-ternary
  const experienceCredit = isLifestyleOnly
    ? "$50"
    : isPremierCollectionEnabled
      ? undefined // we don't display experience credit when its LC and PC
      : "$100";

  // if no earn multiplier do not display a banner or if its less than 0
  if (
    !account ||
    !account?.earn?.hotelsMultiplier ||
    (typeof earn === "number" && earn <= 0)
  ) {
    return null;
  }

  // TODO: handle texts properly once the backend is ready
  switch (history.location.pathname) {
    case PATH_HOME.slice(0, -1):
    case PATH_HOME:
    case PATH_VACATION_RENTALS:
      if (!matchesMobile) {
        contentText = (
          <SanitizedHtml
            as={Typography}
            variant="subtitle2"
            html={constants.PREMIER_COLLECTION_EARN_BANNER_TEXT({
              firstName,
              earnRate,
              productId,
              lastFour,
              currency: currencyDescription || currency,
              isWithLifestyle,
              isLifestyleOnly,
              experienceCredit,
              stayType,
              isUserPrimary:
                customerAccountRole === CustomerAccountRole.Primary,
            })}
          />
        );
      }
      // hide the banner on mobile search workflow
      else {
        contentText = undefined;
      }
      break;
    case PATH_AVAILABILITY.slice(0, -1):
    case PATH_AVAILABILITY:
    case PATH_SHOP.slice(0, -1):
    case PATH_SHOP:
    case PATH_PREMIER_COLLECTION_SHOP_DETAILS:
    case PATH_ANCILLARY_CUSTOMIZE:
    case PATH_VACATION_RENTALS_AVAILABILITY:
    case PATH_VACATION_RENTALS_SHOP.slice(0, -1):
    case PATH_VACATION_RENTALS_SHOP:
      contentText = (
        <SanitizedHtml
          as={Typography}
          variant="subtitle2"
          html={constants.PREMIER_COLLECTION_EARN_BANNER_TEXT({
            firstName,
            earnRate,
            productId,
            lastFour,
            currency: currencyDescription || currency,
            isWithLifestyle,
            isLifestyleOnly,
            experienceCredit,
            stayType,
            isUserPrimary:
              customerAccountRole === CustomerAccountRole.Primary,
          })}
        />
      );
      break;
    case PATH_BOOK.slice(0, -1):
    case PATH_BOOK:
    case PATH_VACATION_RENTALS_BOOK.slice(0, -1):
    case PATH_VACATION_RENTALS_BOOK:
      if (isCorpTenant(config.TENANT)) {
        contentText = (
          <SanitizedHtml
            as={Typography}
            variant="subtitle2"
            html={constants.PREMIER_COLLECTION_EARN_BANNER_TEXT({
              firstName,
              earnRate,
              productId,
              lastFour,
              currency: currencyDescription || currency,
              isWithLifestyle,
              isLifestyleOnly,
              experienceCredit,
              stayType,
              isUserPrimary:
                customerAccountRole === CustomerAccountRole.Primary,
            })}
          />
        );
      }
      break;
  }

  if (!contentText) {
    return null;
  }

  return (
    <GenericBanner
      className="rewards-banner-root"
      content={contentText}
      containerId={B2B_PORTAL_BANNER_CONTAINER_ID}
      isMobile={matchesMobile}
    />
  );
};
